import { backendServices } from '../../../../services';
import { AxisType, GraphType } from '../../../../types';
import { CardsChartData } from '../Charts';
import { SliceData, XAxisDateGroup, YAxisDataDefinition } from '../DashboardGenericReport.types';
import { BarChartData } from '../DashboardGenericReportBar';
import { DonutChartData } from '../DashboardGenericReportDonut';
import { LineChartData } from '../DashboardGenericReportLine';
import { PieChartData } from '../DashboardGenericReportPie';
import { TableChartData } from '../DashboardGenericReportTable';

const convertToPieOrDonutChartData = (
    reportData: backendServices.ViewModels.GenericDashboardReportViewModel[],
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): PieChartData | DonutChartData => {
    const sliceLabelName = metadata.GraphConfiguration.root.keyAxisDefinition?.name ?? undefined;
    const sliceValueName = metadata.GraphConfiguration.root.valueAxisDefinition[0].name;

    if (!sliceLabelName || !sliceValueName) {
        throw new Error('Invalid configuration');
    }

    const slices = reportData.reduce((acc, item) => {
        const value = item[sliceValueName] as number | null;
        const label = item[sliceLabelName] as string;

        acc.push({
            y: value ?? 0,
            name: label,
            label: label,
        });
        return acc;
    }, [] as SliceData[]);

    const chartData: PieChartData | DonutChartData = {
        slices: slices,
    };

    return chartData;
};

export const convertToLineOrBarChartData = (
    reportData: backendServices.ViewModels.GenericDashboardReportViewModel[],
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): LineChartData | BarChartData => {
    const horizontalAxisName = metadata.GraphConfiguration.root.keyAxisDefinition?.name;
    const verticalAxisDefinitions = metadata.GraphConfiguration.root.valueAxisDefinition;

    const horizontalAxisType = metadata.GraphConfiguration.root.keyAxisDefinition?.type;
    const fillLineArea = metadata.GraphConfiguration.root.fillLineArea;
    const hideLegend = metadata.GraphConfiguration.root.hideLegend;

    if (!horizontalAxisName || !verticalAxisDefinitions) {
        throw new Error('Invalid configuration');
    }

    const xAxisLabels = reportData.map((item) => item[horizontalAxisName] as string);

    let xAxisDateGroups: XAxisDateGroup[] | undefined = undefined;
    if (horizontalAxisType === AxisType.DateGroup) {
        const startDate = metadata.GraphConfiguration.root.keyAxisDefinition?.startDate;
        const endDate = metadata.GraphConfiguration.root.keyAxisDefinition?.endDate;

        if (!startDate || !endDate) {
            throw new Error('Invalid configuration');
        }

        xAxisDateGroups = reportData.map((item) => ({
            startDate: item[startDate] as string,
            endDate: item[endDate] as string,
        }));
    }

    const yAxisType = metadata.GraphType === GraphType.Line ? (fillLineArea ? 'area' : 'line') : 'column';

    const yAxisData = verticalAxisDefinitions.map((definition) => {
        const data = xAxisLabels.map((label) => {
            const dataPoint = reportData.find((item) => item[horizontalAxisName] === label);
            const value = dataPoint ? (dataPoint[definition.name] as number | null) : null;
            return value ?? 0;
        });

        return {
            showInLegend: !hideLegend,
            type: yAxisType,
            name: definition.label ?? definition.name, // if label is not provided, use report name
            data,
            marker: { enabled: true },
        };
    }) as YAxisDataDefinition[];

    const chartData: LineChartData | BarChartData = {
        xAxisLabels,
        yAxisData,
        xAxisDateGroups,
    };

    return chartData;
};

export const convertToTableChartData = (
    reportData: backendServices.ViewModels.GenericDashboardReportViewModel[],
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): TableChartData => {
    const columnDefinitions = metadata.GraphConfiguration.root.valueAxisDefinition;

    if (!columnDefinitions) {
        throw new Error('Invalid configuration');
    }

    // TODO: add styling to constants file?
    const columns = columnDefinitions.map((definition) => ({
        field: definition.name,
        headerName: definition.label,
        flex: 2,
        align: 'left',
    })) as TableChartData['columns'];

    const rows = reportData.map((item, index) => {
        const row: { id: number; [key: string]: string | number } = { id: index + 1 };

        columnDefinitions.forEach((definition) => {
            const value = String(item[definition.name]);
            row[definition.name] = value;
        });

        return row;
    });

    const chartData: TableChartData = {
        rows,
        columns,
    };

    return chartData;
};

export const convertToCardsChartData = (
    reportData: backendServices.ViewModels.GenericDashboardReportViewModel[],
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): CardsChartData => {
    const cardDefinitions = metadata.GraphConfiguration.root.valueAxisDefinition;

    if (!cardDefinitions) {
        throw new Error('Invalid configuration');
    }

    const cards = cardDefinitions.reduce((acc, definition) => {
        const title = definition.label ?? definition.name;
        const value = String(reportData[0]?.[definition.name] ?? 0);

        acc.push({
            title,
            value,
        });

        return acc;
    }, [] as CardsChartData['cards']);

    const chartData: CardsChartData = {
        cards,
    };

    return chartData;
};

export const convertToChartData = (
    reportData: backendServices.ViewModels.GenericDashboardReportViewModel[],
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): PieChartData | TableChartData | LineChartData | BarChartData | DonutChartData | CardsChartData => {
    try {
        switch (metadata.GraphType) {
            case GraphType.Pie:
                return convertToPieOrDonutChartData(reportData, metadata);
            case GraphType.Donut:
                return convertToPieOrDonutChartData(reportData, metadata);
            case GraphType.Line:
                return convertToLineOrBarChartData(reportData, metadata);
            case GraphType.Bar:
                return convertToLineOrBarChartData(reportData, metadata);
            case GraphType.Table:
                return convertToTableChartData(reportData, metadata);
            case GraphType.Cards:
                return convertToCardsChartData(reportData, metadata);
            default:
                return {} as
                    | PieChartData
                    | TableChartData
                    | LineChartData
                    | BarChartData
                    | DonutChartData
                    | CardsChartData;
        }
    } catch (error) {
        return {} as PieChartData | TableChartData | LineChartData | BarChartData | DonutChartData | CardsChartData;
    }
};

export const isDataEmpty = (
    chartData: PieChartData | TableChartData | LineChartData | BarChartData | DonutChartData | CardsChartData,
    metadata: backendServices.ViewModels.DashboardReportMetadataViewModel
): boolean => {
    switch (metadata.GraphType) {
        case GraphType.Pie:
            return (chartData as PieChartData).slices.every((slice) => slice.y === 0);
        case GraphType.Donut:
            return (chartData as DonutChartData).slices.every((slice) => slice.y === 0);
        case GraphType.Line:
            return (chartData as LineChartData).yAxisData.every((yAxis) => yAxis.data.every((value) => value === 0));
        case GraphType.Bar:
            return (chartData as BarChartData).yAxisData.every((yAxis) => yAxis.data.every((value) => value === 0));
        case GraphType.Table:
            return false; // NO DATA TO DISPLAY label not supported for Tables
        case GraphType.Cards:
            return false; // NO DATA TO DISPLAY label not supported for Cards
        default:
            throw new Error('Invalid report metadata');
    }
};

/**
 * Backend stores config as XML, so API returns all config fields as strings. Convert them to proper types.
 */
export const parseGraphConfiguration = (
    data: backendServices.ViewModels.GraphConfigurationRawViewModel
): backendServices.ViewModels.GraphConfigurationViewModel => {
    return {
        ...data,
        root: {
            ...data.root,
            graphType: Number(data.root.graphType),
            keyAxisDefinition: data.root.keyAxisDefinition
                ? {
                      ...data.root.keyAxisDefinition,
                      type: Number(data.root.keyAxisDefinition.type),
                  }
                : undefined,
            valueAxisDefinition: data.root.valueAxisDefinition.map((axis: any) => ({
                ...axis,
                type: Number(axis.type),
            })),
            valueAxisMaxValue: data.root.valueAxisMaxValue ? Number(data.root.valueAxisMaxValue) : undefined,
            fillLineArea: data.root.fillLineArea === 'true',
            showPercentageInSlice: data.root.showPercentageInSlice === 'true',
            hideLegend: data.root.hideLegend === 'true',
        },
    };
};
